import { Injectable } from '@angular/core';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { ProfileStateModel } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, filter, Observable, Subject, take, takeUntil } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { RoleState, RoleStateModel } from '../../state/role.state';
import { ProfileState } from '../../state/user-profile.state';
import { ConfigService } from '../config-service/config-service.service';

@Injectable({
  providedIn: 'root',
})
export class MessagingSocketService {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public message$: BehaviorSubject<string> = new BehaviorSubject('');
  public socket: Socket;
  private baseUrl = '';
  public token = '';
  private isUserSubscribed = false;
  roles!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;
  profileStateData!: Observable<ProfileStateModel>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userDetail: any;
  public newMessageEvent$ = new BehaviorSubject<{
    id: string;
    threadId: string;
    senderId: string;
    isAdmin: boolean;
    content: string;
    createdAt: string;
  }>({
    id: '',
    threadId: '',
    senderId: '',
    isAdmin: false,
    content: '',
    createdAt: '',
  });
  public typingEvent$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public messageReadEvent$: BehaviorSubject<{ threadId: string; userId: string; lastReadMessageId: string }> =
    new BehaviorSubject<{ threadId: string; userId: string; lastReadMessageId: string }>({
      threadId: '',
      userId: '',
      lastReadMessageId: '',
    });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(public readonly configService: ConfigService, private readonly store: Store) {
    if (!this.configService.getActiveConfigType().isWorkflow) {
      this.configService
        .getApi('messaging_service')
        .pipe(take(1))
        .subscribe((api) => {
          this.baseUrl = api;
          this.setupSocketConnection();
        });
    }
  }

  private setupSocketConnection() {
    this.token = sessionStorage.getItem('token') as string;
    if (!this.token) {
      console.log('No token found, skipping socket connection');
      return;
    }
    let path = '';
    if (this.baseUrl.includes('localhost')) {
      path = '/ws/messaging';
    } else {
      path = '/api/messaging/ws/messaging';
    }
    this.socket = io(this.baseUrl, {
      path: path,
      transports: ['websocket'],
      extraHeaders: {
        Authorization: 'Bearer ' + this.token,
      },
      auth: {
        token: this.token,
      },
    });

    // Handle connection lifecycle to coordinate user subscription and track connection state
    this.socket.on('connect', () => {
      console.log('Messaging Socket connected:', this.socket.id);
      this.subscribeUser();
    });

    this.socket.on('disconnect', () => {
      console.log('Messaging Socket disconnected');
      this.isUserSubscribed = false;
    });

    this.socket.on('error', (error: unknown) => {
      console.error('Messaging Socket error:', error);
    });

    // emits when a new message is received on any thread
    this.socket.on(
      'new_message',
      (data: {
        id: string;
        threadId: string;
        senderId: string;
        isAdmin: boolean;
        content: string;
        createdAt: string;
      }) => {
        this.newMessageEvent$.next(data);
      },
    );

    // emits when back-office agent is typing on the active thread
    this.socket.on('typing', (data: { threadId: string; userId: string; isTyping: boolean }) => {
      this.typingEvent$.next(data.isTyping);
    });

    // emits when back-office agent reads a message on the active thread
    this.socket.on(
      'message_read',
      (data: { threadId: string; userId: string; lastReadMessageId: string }) => {
        this.messageReadEvent$.next(data);
      },
    );

    // Lazy load state selections to avoid circular dependency
    this.roles = this.store.select(RoleState.getRole);
    this.profileStateData = this.store.select(ProfileState.getProfile);

    // Wait for both profile and roles to be available, then subscribe once
    combineLatest([this.profileStateData, this.roles])
      .pipe(
        filter(([profile, roles]) => !!profile?.id && !!roles),
        take(1), // Only take the first emission when both are ready
        takeUntil(this.destroy$),
      )
      .subscribe(([profile, roles]) => {
        this.userDetail = profile;
        this.roleInfo = roles;
        console.log('[MessagingSocketService] User details and roles loaded, calling subscribeUser');
        this.subscribeUser();
      });
  }

  public joinThread(threadId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('join_thread', threadId);
  }

  public leaveThread(threadId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('leave_thread', threadId);
  }

  // emit this event when user reads a message on active thread
  public emitMessageRead(threadId: string, lastReadMessageId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('mark_read', { threadId, lastReadMessageId });
  }

  // emit this event when user starts typing on active thread
  public emitTypingStartEvent(threadId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('typing_start', { threadId });
  }

  // emit this event when user stops typing on active thread
  public emitTypingStopEvent(threadId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('typing_stop', { threadId });
  }

  private subscribeUser() {
    if (this.isUserSubscribed) {
      console.log('[MessagingSocketService] User already subscribed, skipping');
      return;
    }
    // Check if socket is connected
    if (!this.socket || !this.socket.connected) {
      console.log('[MessagingSocketService] Socket not connected yet, will subscribe on connect event');
      return;
    }
    if (this.token && this.userDetail?.id) {
      const subscribePayload = {
        userId: this.userDetail.id,
        device: this.roleInfo?.device ?? '',
        orgId: this.userDetail.activeOrganisationId,
        email: this.userDetail.email,
        firstName: this.userDetail.firstName,
        lastName: this.userDetail.lastName,
        phoneNumber: this.userDetail.phoneNumber ?? '',
      };
      this.socket.emit('subscribeUser', subscribePayload);
      this.isUserSubscribed = true;
      console.log('[MessagingSocketService] subscribe user successfully');
    } else {
      console.log('[MessagingSocketService] Cannot subscribe user - missing token or userDetail:', {
        hasToken: !!this.token,
        hasUserDetail: !!this.userDetail?.id,
      });
    }
  }

  /**
   * Disconnect socket and clean up event listeners
   * Call this method on user logout
   */
  public disconnect(): void {
    if (this.socket) {
      console.log('Disconnecting socket and cleaning up event listeners');

      // Remove all event listeners
      this.socket.off('connect');
      this.socket.off('disconnect');
      this.socket.off('error');
      this.socket.off('new_message');
      this.socket.off('typing');
      this.socket.off('message_read');

      // Close the socket connection and prevent reconnection
      this.socket.disconnect();

      // Remove all listeners to prevent memory leaks
      this.socket.removeAllListeners();
    }
  }
}
